/**
 * useCompany
 *
 * WEB-002 fix: GET /api/company was firing 3x on a single dashboard load
 * because three different places each issued their own independent fetch
 * for the same data — Navigation.tsx and CompanyBrandLogo.tsx each ran a
 * raw `useEffect` + `api.get('/api/company')`, and useCountryProfile.ts ran
 * its own `useQuery({ queryKey: ['company'], ... })`. None of them shared a
 * cache entry with the others (two weren't using React Query at all), so
 * every mount was a fresh network round-trip.
 *
 * Follow-up (QA WEB-002, 2026-09-18): a *second* wave of call sites — email
 * modals, tax breakdown, agreements, the calendar route optimizer, the admin
 * feedback view, AddressFieldsEditor, AuthContext — each ran their own
 * `useQuery({ queryKey: ['company'], queryFn: () => api.get('/api/company') })`
 * with the default staleTime (0). Even though the queryKey matched, an
 * observer with staleTime 0 treats the cached data as stale on mount and
 * triggers its own background refetch — so the request count didn't drop
 * to 1 until every caller shared the *same* staleTime via this hook.
 *
 * This hook is the single place that fetches `/api/company`. Every caller
 * that needs company/tenant fields (name, iconUrl, websiteUrl, countryCode,
 * timeZone, settings, ...) should use this hook — React Query dedupes
 * concurrent callers that share the same queryKey, so mounting it from
 * several components on the same page still issues exactly one request.
 *
 * Non-hook callers (AuthContext, AddressFieldsEditor's mutation path, or any
 * plain function outside a component) should use `companyQueryOptions` with
 * `queryClient.fetchQuery(companyQueryOptions)` — it returns the cached value
 * when still fresh instead of always issuing a new request.
 *
 * @module hooks/useCompany
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { useOptionalAuth } from '@/context/AuthContext'

/** Loosely-typed shape of the /api/company response — callers narrow to the fields they need. */
export interface CompanyProfile {
  name?: string | null
  iconUrl?: string | null
  websiteUrl?: string | null
  countryCode?: string | null
  timeZone?: string | null
  settings?: Record<string, any>
  [key: string]: any
}

/** Shared queryKey — keep this exact tuple everywhere so invalidations keep working. */
export const COMPANY_QUERY_KEY = ['company'] as const

/** 5 min — company/tenant profile rarely changes mid-session; edits invalidate this key explicitly. */
export const COMPANY_STALE_TIME = 5 * 60 * 1000
export const COMPANY_GC_TIME = 10 * 60 * 1000

/**
 * Plain query-options object (queryKey + queryFn + staleTime/gcTime) for callers
 * that aren't inside a component — e.g. `queryClient.fetchQuery(companyQueryOptions)`.
 */
export const companyQueryOptions = {
  queryKey: COMPANY_QUERY_KEY,
  queryFn: () => api.get<CompanyProfile>('/api/company'),
  staleTime: COMPANY_STALE_TIME,
  gcTime: COMPANY_GC_TIME,
}

/**
 * @param options Optional overrides merged on top of the shared defaults — e.g.
 *   `{ queryFn: () => api.get('/api/company').catch(() => null) }` to preserve a
 *   caller's "tolerate failure" semantics, or `{ refetchOnMount: 'always' }` for
 *   an editor that must show current data every time it opens. queryKey/staleTime/
 *   gcTime are never overridable — keep every observer on the same cache entry.
 *
 * `enabled` is gated on `isAuthenticated` (mirrors `useEntitlements()` in
 * `useEntitlements.ts`) — a logged-out visitor must never issue an authenticated
 * `GET /api/company`. WEB-BUG (2026-09-26): `UpgradeDialogProvider` (mounted
 * app-wide, including public auth pages like /reset-password) calls
 * `useFormatters()` → `useCompany()` unconditionally; without this gate that
 * fired a 401 on every logged-out page load, which api.ts's global 401 handler
 * turned into a hard redirect to /login before the user could see the page.
 * A caller-supplied `options.enabled` is ANDed with the auth check, never
 * overrides it.
 */
export function useCompany<T = CompanyProfile>(
  options?: Partial<Omit<UseQueryOptions<T>, 'queryKey' | 'staleTime' | 'gcTime'>>
) {
  const auth = useOptionalAuth()
  const isAuthenticated = !!auth?.user

  return useQuery<T>({
    queryFn: () => api.get<T>('/api/company'),
    ...options,
    queryKey: COMPANY_QUERY_KEY,
    staleTime: COMPANY_STALE_TIME,
    gcTime: COMPANY_GC_TIME,
    enabled: (options?.enabled ?? true) && isAuthenticated,
  })
}
